import { Injectable } from '@angular/core';
import {
  SynonymMap,
  TypoMap,
  QuestionPattern,
} from './models/searchable-item.model';

/**
 * Lazy-loads search data files via dynamic import().
 * Webpack/esbuild emits a separate chunk per JSON, so nothing is in the
 * initial bundle until a search actually runs. Results are cached as
 * Promises so concurrent first-time callers don't double-fetch.
 *
 * Domain providers reuse this for their own keyword JSONs by passing a
 * loader factory + cache key.
 */
@Injectable({ providedIn: 'root' })
export class SearchDataLoaderService {
  private cache = new Map<string, Promise<unknown>>();

  loadBaseSynonyms(lang: string): Promise<SynonymMap> {
    const key = `synonyms.${lang}`;
    if (!this.cache.has(key)) {
      const loader =
        lang === 'de'
          ? () => import('./data/synonyms.de.json').then((m) => m.default as SynonymMap)
          : () => import('./data/synonyms.en.json').then((m) => m.default as SynonymMap);
      this.cache.set(key, loader());
    }
    return this.cache.get(key) as Promise<SynonymMap>;
  }

  loadBaseTypos(lang: string): Promise<TypoMap> {
    const key = `typos.${lang}`;
    if (!this.cache.has(key)) {
      const loader =
        lang === 'de'
          ? () => import('./data/typos.de.json').then((m) => m.default as TypoMap)
          : () => import('./data/typos.en.json').then((m) => m.default as TypoMap);
      this.cache.set(key, loader());
    }
    return this.cache.get(key) as Promise<TypoMap>;
  }

  loadQuestionPatterns(): Promise<QuestionPattern[]> {
    const key = 'question-patterns';
    if (!this.cache.has(key)) {
      this.cache.set(
        key,
        import('./data/question-patterns.json').then(
          (m) => m.default as QuestionPattern[]
        )
      );
    }
    return this.cache.get(key) as Promise<QuestionPattern[]>;
  }

  /** Compound-splitter dictionary (German). Returns ReadonlySet for O(1) lookup. */
  loadCommonWords(lang: string): Promise<ReadonlySet<string>> {
    const key = `common-words.${lang}`;
    if (!this.cache.has(key)) {
      // Currently only DE has a compound dictionary; EN compounds are less productive.
      const loader =
        lang === 'de'
          ? () =>
              import('./data/common-words.de.json').then(
                (m) => new Set(m.default as string[]) as ReadonlySet<string>
              )
          : () => Promise.resolve(new Set<string>() as ReadonlySet<string>);
      this.cache.set(key, loader());
    }
    return this.cache.get(key) as Promise<ReadonlySet<string>>;
  }

  /**
   * Generic loader used by domain providers for their per-domain keyword JSON.
   * Caches by cacheKey; the loader factory is only called on cache miss.
   */
  loadProviderData<T>(cacheKey: string, loader: () => Promise<T>): Promise<T> {
    if (!this.cache.has(cacheKey)) {
      this.cache.set(cacheKey, loader());
    }
    return this.cache.get(cacheKey) as Promise<T>;
  }

  /** Drop cached promises matching predicate (e.g. on language change). */
  invalidate(predicate?: (key: string) => boolean): void {
    if (!predicate) {
      this.cache.clear();
      return;
    }
    for (const key of Array.from(this.cache.keys())) {
      if (predicate(key)) this.cache.delete(key);
    }
  }
}
